/** @format */

import {useEffect, useMemo, useRef} from 'react';

import {Box, LinearProgress, Typography} from '@mui/material';
import {Trans} from 'react-i18next';
import {useNavigate, useSearchParams} from 'react-router-dom';

import {format} from 'date-fns';

import {useAuthContext} from 'contexts/AuthContext';
import {useMixpanelContext} from 'contexts/MixpanelContext';

import {ScreenLayout} from 'layouts/ScreenLayout';

import {Header} from './components/Header';
import {ProjectCard} from './components/ProjectCard';
import {filterProjectsByKpi} from './helpers/filterProjectsByKpi';
import {filterProjectsBySearch} from './helpers/filterProjectsBySearch';
import {getKpiFilterOptions} from './helpers/getKpiFilterOptions';
import {mapProjectCard} from './helpers/mapProjectCard';
import {trackOverviewEvent} from './helpers/trackOverviewEvent';
import {HomeOverviewClasses as classes, HomeOverviewRoot as Root} from './styles';
import {useOverviewProjectsQuery} from './queries/__generated__';
import {ViewMode} from './types';

const VIEW_MODES: ViewMode[] = ['summary', 'dynamic'];

const HomeOverview = () => {
  // Один общий «сегодня» на весь рендер: useRef фиксирует дату на маунте (исправляет
  // stale-`now` баг прежнего module-scope const) и питает и list-запрос, и mapProjectCard.
  const now = useRef(new Date()).current;
  const today = format(now, 'yyyy-MM-dd');

  const [searchParams, setSearchParams] = useSearchParams();

  const rawViewMode = searchParams.get('viewMode') ?? '';
  const viewMode: ViewMode = VIEW_MODES.includes(rawViewMode as ViewMode)
    ? (rawViewMode as ViewMode)
    : 'dynamic';
  const kpiFilter = searchParams.get('kpiFilter') ?? 'all';
  const search = searchParams.get('search') ?? '';

  const {authToken} = useAuthContext();
  const navigate = useNavigate();
  const {mixpanel} = useMixpanelContext();

  // Fire once on mount — captures the initial view mode read from URL.
  const mixpanelRef = useRef(mixpanel);
  useEffect(() => {
    trackOverviewEvent(mixpanelRef.current, 'overview_page_view', {view_mode: viewMode});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setParam = (key: string, value: string) =>
    setSearchParams(
      prev => {
        const params = new URLSearchParams(prev);
        params.set(key, value);
        return params;
      },
      {replace: true},
    );

  const handleChangeViewMode = (mode: ViewMode) => setParam('viewMode', mode);
  const handleChangeKpiFilter = (kpi: string) => setParam('kpiFilter', kpi);
  const handleChangeSearch = (value: string) => setParam('search', value);

  const {data, loading} = useOverviewProjectsQuery({
    skip: !authToken,
    variables: {date: today},
  });

  const companies = useMemo(() => data?.currentUser?.companies ?? [], [data]);

  const allCards = useMemo(
    () => companies.map(company => mapProjectCard(company, today)),
    [companies, today],
  );
  // KPI options come from the FULL list (not the search-filtered subset), so a persisted kpiFilter
  // stays selectable even when search hides every project that has it. The KPI filter then hides
  // whole projects that lack the selected KPI — it does NOT drop rows inside a card.
  const kpiOptions = useMemo(() => getKpiFilterOptions(allCards), [allCards]);
  const visibleCards = useMemo(
    () => filterProjectsByKpi(filterProjectsBySearch(allCards, search), kpiFilter),
    [allCards, search, kpiFilter],
  );

  const handleAddProject = () => {
    navigate('/home/project/wizard/new');
  };

  return (
    <Root>
      <ScreenLayout>
        <Box className={classes.header}>
          <Header
            viewMode={viewMode}
            onChangeViewMode={handleChangeViewMode}
            kpiFilter={kpiFilter}
            kpiOptions={kpiOptions}
            onChangeKpiFilter={handleChangeKpiFilter}
            search={search}
            onChangeSearch={handleChangeSearch}
            onAddProject={handleAddProject}
          />
        </Box>

        {loading ? (
          <Box className={classes.loader} data-testid='loading'>
            <LinearProgress />
          </Box>
        ) : visibleCards.length === 0 ? (
          <Box className={classes.empty}>
            <Typography>
              <Trans>Проектов нет</Trans>
            </Typography>
          </Box>
        ) : (
          <Box className={classes.projectList} data-testid='project-list'>
            {visibleCards.map(card => (
              <ProjectCard key={card.companyId} model={card} viewMode={viewMode} now={now} />
            ))}
          </Box>
        )}
      </ScreenLayout>
    </Root>
  );
};

export default HomeOverview;
